import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import SEO from './SEO';
import './HowItWorksPage.css';

// 1. Upload Visualizer Sub-component
const UploadVisual = () => {
  const [progress, setProgress] = useState(0);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setComplete(true);
          return 100;
        }
        return prev + 5;
      });
    }, 150);

    if (complete) {
      clearInterval(timer);
      const resetTimeout = setTimeout(() => {
        setProgress(0);
        setComplete(false);
      }, 2500);
      return () => clearTimeout(resetTimeout);
    }

    return () => clearInterval(timer);
  }, [complete]);

  return (
    <div className="mock-upload-box">
      <svg className="mock-upload-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
      <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '4px', color: complete ? '#10b981' : 'inherit', transition: 'color 0.3s' }}>
        {complete ? '✓ candidate_resume.pdf' : 'candidate_resume.pdf'}
      </div>
      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
        {complete ? 'Successfully parsed and synced!' : `Parsing resume structure... ${progress}%`}
      </div>
      <div className="mock-upload-progress">
        <div className="mock-upload-progress-bar" style={{ width: `${progress}%`, backgroundColor: complete ? '#10b981' : '#3b82f6', transition: 'width 0.15s linear, background-color 0.3s' }}></div>
      </div>
    </div>
  );
};

// 2. AI Prep Console Sub-component
const ConsoleVisual = () => {
  const [lineCount, setLineCount] = useState(0);
  const fullLines = [
    "> Loading tenant database questions...",
    "> Found candidate skills: React, Node.js, SQL",
    "> Generating technical prompt matching resume...",
    "> [AI Prompt Ready]: Explain DB indexing and GCM cipher mode.",
    "> Rubric set: Technical: 50%, Behavioral: 50%"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setLineCount((prev) => {
        if (prev >= fullLines.length) {
          return 0; // Reset and loop!
        }
        return prev + 1;
      });
    }, 1500);

    return () => clearInterval(interval);
  }, []);

  const visibleLines = fullLines.slice(0, lineCount);

  return (
    <div className="mock-prep-console">
      {visibleLines.map((line, idx) => (
        <div key={idx} className="mock-console-line" style={{
          color: line && line.includes('[AI Prompt Ready]') ? '#10b981' : line && line.includes('Rubric set') ? '#f59e0b' : '#38bdf8'
        }}>
          {line}
        </div>
      ))}
      <span className="mock-console-cursor">_</span>
    </div>
  );
};

// 3. Live Interview Video Sub-component
const InterviewVisual = () => {
  return (
    <div className="mock-video-wrapper">
      <div className="mock-video-card">
        <div className="mock-video-avatar">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        </div>
        <div className="mock-audio-wave">
          <div className="mock-wave-bar"></div>
          <div className="mock-wave-bar"></div>
          <div className="mock-wave-bar"></div>
        </div>
        <span className="mock-video-label">AI Interviewer</span>
      </div>
      <div className="mock-video-card">
        <div className="mock-video-avatar" style={{ background: 'linear-gradient(135deg, #10b981 0%, #1e293b 100%)' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        </div>
        <div className="mock-pulse-dot"></div>
        <span className="mock-video-label">Candidate (You)</span>
      </div>
      <div className="mock-interview-dialog">
        <strong style={{ color: '#3b82f6' }}>AI Assistant:</strong> "Tell me about a time you solved a complex concurrency bug in production."
      </div>
    </div>
  );
};

// 4. Monitoring Panel Sub-component
const MonitorVisual = () => {
  const [alertTriggered, setAlertTriggered] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setAlertTriggered((prev) => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="mock-monitor-panel">
      <div className="mock-monitor-header">
        <span>Security Monitor</span>
        <span style={{ color: alertTriggered ? '#f59e0b' : '#10b981', transition: 'color 0.3s' }}>
          {alertTriggered ? 'Warning' : 'Active'}
        </span>
      </div>
      <div className="mock-monitor-status">
        <span>👤 Candidate presence check</span>
        <span style={{ color: '#10b981', fontWeight: '600' }}>Verified</span>
      </div>
      <div className="mock-monitor-status">
        <span>🖥️ Window focus active</span>
        <span style={{ color: '#10b981', fontWeight: '600' }}>Normal</span>
      </div>
      <div className={`mock-monitor-status ${alertTriggered ? 'alert' : ''}`}>
        <span>⚠️ External tab attempt</span>
        <span style={{ color: alertTriggered ? '#ef4444' : '#10b981', fontWeight: '600', transition: 'color 0.3s' }}>
          {alertTriggered ? 'Flagged' : 'None'}
        </span>
      </div>
    </div>
  );
};

// 5. Report Dashboard Sub-component
const ReportVisual = () => {
  return (
    <div className="mock-report-dashboard">
      <div className="mock-candidate-header">
        <div className="mock-candidate-pic">JD</div>
        <div style={{ flexGrow: 1 }}>
          <div style={{ fontWeight: '600', fontSize: '13px' }}>John Doe</div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Software Engineer Application</div>
        </div>
        <div className="mock-report-badge">Report PDF</div>
      </div>
      <div className="mock-score-grid">
        <div className="mock-score-card">
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Overall Score</div>
          <div className="mock-score-num">94%</div>
        </div>
        <div className="mock-score-card">
          <div style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Integrity</div>
          <div className="mock-score-num" style={{ color: '#10b981' }}>High</div>
        </div>
      </div>
    </div>
  );
};

const stepsData = [
  {
    step: "01",
    title: "Candidate Upload",
    description: "Candidate uploads resume to the platform.",
    bullets: [
      "Uploads PDF, DOCX, or text-based resumes seamlessly",
      "Automated parsing of contact details, work history, and skills",
      "Instant profile synchronization with tenant-isolated database"
    ],
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
        <polyline points="17 8 12 3 7 8"></polyline>
        <line x1="12" y1="3" x2="12" y2="15"></line>
      </svg>
    )
  },
  {
    step: "02",
    title: "AI Prep",
    description: "AI generates personalized interview questions.",
    bullets: [
      "Cross-references candidate experience with targeted job description",
      "Generates tailored behavioral and technical questions dynamically",
      "Establishes standardized grading rubrics for consistent assessment"
    ],
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M12 16v-4"></path>
        <path d="M12 8h.01"></path>
        <path d="M9 16c.3-3 1.5-4 3-4s2.7 1 3 4"></path>
      </svg>
    )
  },
  {
    step: "03",
    title: "Live Interview",
    description: "AI conducts a seamless real-time interview.",
    bullets: [
      "High-definition WebRTC video & audio communication channels",
      "Real-time synchronized coding pad and shared whiteboard canvas",
      "AI companion prompt delivery with interactive speech-to-text transcriptions"
    ],
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 7l-7 5 7 5V7z"></path>
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
      </svg>
    )
  },
  {
    step: "04",
    title: "Monitoring",
    description: "Platform monitors candidate activity for integrity.",
    bullets: [
      "Active tab-tracking triggers prompt warnings on window-switching",
      "Visual face verification flags background changes or extra people",
      "Integrity level indicators maintain solid proctoring credibility"
    ],
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
        <circle cx="12" cy="12" r="3"></circle>
      </svg>
    )
  },
  {
    step: "05",
    title: "Report Generation",
    description: "Detailed recruiter report is generated instantly.",
    bullets: [
      "Comprehensive AI score breakdown with technical vs soft skill grading",
      "Word-for-word conversation transcript searchable by timeline tags",
      "Security integrity logs summarizing test behavior and proctoring stats"
    ],
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
    )
  }
];

// FAQ Accordion sub-component for Content and On-Page SEO
const FAQAccordion = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: "How does the AI Conversational Interviewer screen candidates?",
      a: "The AI Interviewer reads the candidate's resume, cross-references it with your job description, and initiates a natural speech conversation. It dynamically generates tailored technical and behavioral questions in real-time, grading their responses based on your organization's custom rubrics."
    },
    {
      q: "What security standards protect candidate and organization data?",
      a: "Our platform leverages an enterprise-grade multi-tenant isolated database architecture. Each registered company receives a dedicated, isolated database container instance (e.g. SQLite database file or MySQL schema). All sensitive candidate details, transcripts, and submissions are stored in an encrypted format using AES-256-GCM."
    },
    {
      q: "How does smart proctoring detect cheating without plugins?",
      a: "We use browser-native MediaPipe facial mesh mapping to monitor head pose, face direction, and look-away duration. We also monitor browser tab visibility triggers to track window-focus violations. All proctoring occurs strictly inside the browser without requiring local machine installations."
    },
    {
      q: "Can interviewers review candidate live coding trials?",
      a: "Yes! Every single character entered in our Monaco-powered IDE is recorded chronologically. Interviewers can review candidate coding trials keystroke-by-keystroke in a visual media player (complete with play/pause and speed dials), allowing you to audit real problem-solving pipelines."
    }
  ];

  return (
    <section className="how-faq-section scroll-reveal" style={{ maxWidth: '900px', margin: '80px auto', padding: '0 20px' }}>
      <h2 style={{ fontSize: '32px', textAlign: 'center', marginBottom: '40px', fontWeight: '700', color: 'var(--text-primary)' }}>Frequently Asked Questions</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {faqs.map((faq, idx) => {
          const isOpen = openIndex === idx;
          return (
            <div 
              key={idx} 
              style={{ 
                background: 'var(--bg-card)', 
                border: '1px solid var(--border-light)', 
                borderRadius: '12px', 
                overflow: 'hidden', 
                transition: 'all 0.3s ease',
                boxShadow: isOpen ? '0 10px 30px rgba(79, 70, 229, 0.08)' : 'none'
              }}
            >
              <button 
                onClick={() => setOpenIndex(isOpen ? null : idx)}
                style={{ 
                  width: '100%', 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  background: 'none', 
                  border: 'none', 
                  padding: '20px 24px', 
                  cursor: 'pointer', 
                  textAlign: 'left',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  fontFamily: 'inherit'
                }}
              >
                <span>{faq.q}</span>
                <span style={{ fontSize: '20px', transform: isOpen ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s ease', color: 'var(--accent)', fontWeight: 'bold' }}>+</span>
              </button>
              <div 
                style={{ 
                  maxHeight: isOpen ? '200px' : '0', 
                  overflow: 'hidden', 
                  transition: 'max-height 0.3s cubic-bezier(0, 1, 0, 1)',
                  padding: isOpen ? '0 24px 20px 24px' : '0 24px',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  lineHeight: '1.6'
                }}
              >
                {faq.a}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const HowItWorksPage = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isLoggedIn = !!user && !!user.id;
  const userName = user.full_name || '';

  const pageRef = useRef(null);
  const navigate = useNavigate();

  // Setup lightweight scroll-reveal animations using IntersectionObserver
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px -10% -10% 0px',
      threshold: 0.1
    };

    const handleIntersection = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          // Once animated, stop observing this element
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, observerOptions);
    const revealElements = pageRef.current?.querySelectorAll('.scroll-reveal');

    if (revealElements) {
      revealElements.forEach(el => observer.observe(el));
    }

    return () => {
      if (revealElements) {
        revealElements.forEach(el => observer.unobserve(el));
      }
    };
  }, []);

  // Map step index to specific sub-component visuals
  const renderVisualMock = (idx) => {
    switch (idx) {
      case 0:
        return <UploadVisual />;
      case 1:
        return <ConsoleVisual />;
      case 2:
        return <InterviewVisual />;
      case 3:
        return <MonitorVisual />;
      case 4:
        return <ReportVisual />;
      default:
        return null;
    }
  };

  return (
    <div className="how-container" ref={pageRef}>
      <SEO 
        title="How It Works - Step-by-Step Screening Workflow"
        description="Learn how Smart Interview operates: from resume upload and personalized AI question generation to live assessments with automated reports."
        keywords="screening workflow, candidate experience, assessment report, WebRTC video interview, tech screening, hiring timeline"
      />

      {/* Schema.org FAQPage Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How does the AI Conversational Interviewer screen candidates?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "The AI Interviewer reads the candidate's resume, cross-references it with your job description, and initiates a natural speech conversation. It dynamically generates tailored technical and behavioral questions in real-time, grading their responses based on your organization's custom rubrics."
              }
            },
            {
              "@type": "Question",
              "name": "What security standards protect candidate and organization data?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Our platform leverages an enterprise-grade multi-tenant isolated database architecture. Each registered company receives a dedicated, isolated database container instance (e.g. SQLite database file or MySQL schema). All sensitive candidate details, transcripts, and submissions are stored in an encrypted format using AES-256-GCM."
              }
            },
            {
              "@type": "Question",
              "name": "How does smart proctoring detect cheating without plugins?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We use browser-native MediaPipe facial mesh mapping to monitor head pose, face direction, and look-away duration. We also monitor browser tab visibility triggers to track window-focus violations. All proctoring occurs strictly inside the browser without requiring local machine installations."
              }
            },
            {
              "@type": "Question",
              "name": "Can interviewers review candidate live coding trials?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes! Every single character entered in our Monaco-powered IDE is recorded chronologically. Interviewers can review candidate coding trials keystroke-by-keystroke in a visual media player (complete with play/pause and speed dials), allowing you to audit real problem-solving pipelines."
              }
            }
          ]
        })}
      </script>
      <Navbar isLoggedIn={isLoggedIn} userName={userName} />

      {/* Main Container */}
      <main className="how-content">
        
        {/* Step Banner */}
        <div className="how-header-banner scroll-reveal">
          <div className="how-banner-left">Process Workflow</div>
        </div>

        {/* Title Section */}
        <section className="how-title-section scroll-reveal">
          <h1>
            How It <span className="how-title-gradient">Works</span>
          </h1>
          <p>
            Experience a secure, automated screening platform designed to evaluate talent efficiently using isolated databases and state-of-the-art AI.
          </p>
        </section>

        {/* Vertical alternating workflow steps */}
        <section className="how-timeline-wrapper">
          <div className="how-timeline-line"></div>
          
          {stepsData.map((step, idx) => (
            <div key={idx} className="how-section-row scroll-reveal">
              {/* Center timeline dot showing step number */}
              <div className="how-timeline-dot">{step.step}</div>

              {/* Text Description Column */}
              <div className="how-text-col">
                <div className="how-card-icon">{step.icon}</div>
                <h2>{step.title}</h2>
                <p>{step.description}</p>
                <ul className="how-showcase-bullets">
                  {step.bullets.map((bullet, bIdx) => (
                    <li key={bIdx}>
                      <span className="how-bullet-check">✓</span>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Visual Mockup Column */}
              <div className="how-visual-col">
                <div className="how-showcase-visual">
                  {renderVisualMock(idx)}
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Frequently Asked Questions Accordion Section */}
        <FAQAccordion />

        {/* Call to Action (CTA) Section */}
        <section className="how-cta-section scroll-reveal">
          <div className="how-cta-card">
            <h2>Ready to accelerate your hiring?</h2>
            <p>
              Experience secure, isolated data environments and lightning-fast AI screening. Get set up in minutes.
            </p>
            <div className="how-cta-buttons">
              <button className="how-cta-btn primary" onClick={() => navigate('/signup')}>
                Get Started
              </button>
              <button className="how-cta-btn secondary" onClick={() => navigate('/login')}>
                Sign In
              </button>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
};

export default HowItWorksPage;
